<script setup>
// Bases de conhecimento do agente (PowerData).
//
// A ligação é N:N e mora em `power_data_connections`, chaveada pelo SLUG do agente. O
// retrieve do PowerData resolve TODAS as bases conectadas e busca no conjunto — então
// conectar a segunda base não exige nada além de uma segunda conexão. É por isso que esta
// tela liga e desliga bases uma a uma em vez de escolher "a" base.
//
// O aviso de "tool no formato n8n" SAIU daqui, e a razão importa mais que a remoção: ele era
// FALSO, e do jeito pior. Ele olhava o `url_template` do CATÁLOGO — que continua sendo o
// `=https://api.chat-data.com/...{{ $json.chatbotId }}` semeado — e concluía que o runtime
// receberia aquilo. Não recebe: o publish reescreve (`knowledge_tool_publicavel`), e o que
// chega é `.../agents/<slug>/rag/search`. Conferido na revisão publicada.
//
// Ou seja: a tela dizia "as bases conectadas não são consultadas" enquanto elas ERAM, em todo
// agente, sempre. Alarme que grita sem motivo é pior que alarme nenhum — ensina a ignorar o
// âmbar, e o próximo âmbar vai ser verdadeiro.
//
// O aviso que fica é o outro, e esse continua real: base conectada e NENHUMA tool de busca
// ligada. Hoje isso não deveria acontecer (a sincronização liga sozinha ao conectar), o que
// faz dele exatamente o que um aviso tem de ser — a evidência de que algo saiu do lugar.
import { ref, computed, inject, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { usePermissions } from 'dashboard/composables/usePermissions';
import { useAcesso } from 'dashboard/composables/useAcesso';
import powerAgentsApi from 'dashboard/api/powerAgents';
import Button from 'dashboard/components-next/button/Button.vue';
import PowerDataModal from 'dashboard/routes/dashboard/settings/powerData/components/PowerDataModal.vue';
import SectionShell from './SectionShell.vue';
import { mensagemDoErro } from '../erros';

const props = defineProps({
  agent: { type: Object, required: true },
});

const { t } = useI18n();
const router = useRouter();
const ctx = inject('powerAgentContext');
// Criar base, desde 05/10/2026, é do administrador da conta e de quem recebeu `powerdata_manage`
// num papel personalizado — a mesma regra da lista do PowerData e de
// `PowerDataController#exigir_papel_da_acao`, para "Nova base" significar a mesma coisa nas duas
// telas. Quem não cria lê que pede a base ao administrador. Abrir a base vale para todos que veem
// a seção: a rota do PowerData aceita o administrador e o atendente em toda conta, sem flag.
const { isAdmin, isSuperAdmin } = useAdmin();
const { can } = usePermissions();
// ACESSO-01 (L8b): com a chave ACESSO_GRANULAR ligada, o `powerdata:manage` (`power_data#create`), o escopo que o
// servidor pede para criar base; desligada, o papel, como acima.
const { decide } = useAcesso();
const podeCriarBase = computed(
  () =>
    isSuperAdmin.value ||
    decide('powerdata:manage', () => isAdmin.value || can('powerdata_manage'))
);

const modalRef = ref(null);

function abrirBase(store) {
  router.push({
    name: 'power_data_show',
    params: { accountId: ctx.accountId(), storeId: store.id },
  });
}

function novaBase() {
  modalRef.value?.open();
}

const stores = ref([]);
const encriptadasConectadas = ref([]);
const carregando = ref(true);
const mexendo = ref({});
const toolsDoAgente = ref([]);

// O cruzamento é por `encrypted_id`, NÃO por id.
//
// Os dois lados têm espaços de id diferentes: o Rails guarda uma tabela sombra de
// `power_data` com autoincremento próprio (é ela que a conexão referencia), e a listagem de
// bases é proxy do agent-admin, com os ids dele. Comparar id com id dava "nenhuma base
// conectada" com a base conectada — e coincidia enquanto as duas tabelas cresciam juntas,
// que foi o que escondeu isso por um tempo.
const conectadasSet = computed(
  () => new Set((encriptadasConectadas.value || []).map(String))
);
const listaConectada = computed(() =>
  stores.value.filter(s => conectadasSet.value.has(String(s.encrypted_id)))
);
const listaDisponivel = computed(() =>
  stores.value.filter(s => !conectadasSet.value.has(String(s.encrypted_id)))
);

const toolDeConhecimento = computed(() =>
  toolsDoAgente.value.find(b => b?.tool?.name === 'query_knowledge')
);

async function carregar() {
  carregando.value = true;
  try {
    const [lista, doAgente, tools] = await Promise.all([
      powerAgentsApi.knowledgeStores(ctx.accountId()),
      powerAgentsApi.knowledgeOfAgent(ctx.accountId(), props.agent.slug),
      powerAgentsApi.toolsIndex(ctx.accountId(), ctx.agentId()).catch(() => []),
    ]);
    stores.value = Array.isArray(lista) ? lista : lista?.power_data || [];
    encriptadasConectadas.value = doAgente?.power_data_encrypted_ids || [];
    toolsDoAgente.value = Array.isArray(tools) ? tools : tools?.bindings || [];
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.KNOWLEDGE.LOAD_ERROR')));
  } finally {
    carregando.value = false;
  }
}

// Ligar e desligar a base também liga e desliga a ferramenta de busca do agente, no servidor. Sem
// reler, o aviso "este agente não tem a ferramenta de conhecimento ligada" aparecia logo depois de
// conectar e só sumia ao recarregar a página (Gate 4 da POWERDATA-ACESSO-01).
async function recarregarTools() {
  const tools = await powerAgentsApi
    .toolsIndex(ctx.accountId(), ctx.agentId())
    .catch(() => null);
  if (tools) {
    toolsDoAgente.value = Array.isArray(tools) ? tools : tools?.bindings || [];
  }
}

async function ligar(store) {
  mexendo.value = { ...mexendo.value, [store.id]: true };
  try {
    await powerAgentsApi.knowledgeConnect(
      ctx.accountId(),
      store.id,
      props.agent.slug
    );
    encriptadasConectadas.value = [
      ...encriptadasConectadas.value,
      store.encrypted_id,
    ];
    useAlert(t('POWER_AGENTS.KNOWLEDGE.CONNECTED', { nome: store.name }));
    await recarregarTools();
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.KNOWLEDGE.ERROR')));
  } finally {
    mexendo.value = { ...mexendo.value, [store.id]: false };
  }
}

async function desligar(store) {
  mexendo.value = { ...mexendo.value, [store.id]: true };
  try {
    await powerAgentsApi.knowledgeDisconnect(
      ctx.accountId(),
      store.id,
      props.agent.slug
    );
    encriptadasConectadas.value = encriptadasConectadas.value.filter(
      id => String(id) !== String(store.encrypted_id)
    );
    useAlert(t('POWER_AGENTS.KNOWLEDGE.DISCONNECTED', { nome: store.name }));
    await recarregarTools();
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.KNOWLEDGE.ERROR')));
  } finally {
    mexendo.value = { ...mexendo.value, [store.id]: false };
  }
}

onMounted(carregar);
</script>

<template>
  <SectionShell
    data-busca="knowledge"
    :title="t('POWER_AGENTS.KNOWLEDGE.TITLE')"
    :description="t('POWER_AGENTS.KNOWLEDGE.DESCRIPTION')"
    :saving="null"
  >
    <!-- Estado real do caminho até a base. Sem isto, uma base conectada parece uma base
         consultada — e não é a mesma coisa. -->
    <div
      v-if="!carregando && listaConectada.length && !toolDeConhecimento"
      class="flex flex-wrap items-center gap-3 px-4 py-3 border rounded-xl border-n-amber-6 bg-n-amber-2"
    >
      <i class="text-base i-lucide-triangle-alert text-n-amber-10" />
      <p class="flex-1 text-xs min-w-48 text-n-amber-11">
        {{ t('POWER_AGENTS.KNOWLEDGE.TOOL_MISSING') }}
      </p>
    </div>

    <div v-if="carregando" class="py-6 text-xs text-center text-n-slate-10">
      {{ t('POWER_AGENTS.LOADING') }}
    </div>

    <template v-else>
      <!-- Bases ligadas a este agente -->
      <div class="flex flex-col gap-2">
        <p class="text-xs font-medium tracking-wide uppercase text-n-slate-11">
          {{ t('POWER_AGENTS.KNOWLEDGE.CONNECTED_TITLE') }}
        </p>

        <div
          v-if="!listaConectada.length"
          class="flex flex-col items-center gap-1 px-4 py-6 text-center border rounded-xl border-n-amber-6 bg-n-amber-2"
        >
          <!-- Âmbar, e não o cinza tracejado de "vazio". Agente sem base não é um estado
               neutro à espera de configuração: é um agente que responde de memória sobre
               produto e preço, com cara de fonte. Medido em 2026-09-08 — 5 de 5 respostas de
               preço vieram certas sem consultar nada, que é a forma mais difícil de ver. -->
          <i class="text-lg i-lucide-triangle-alert text-n-amber-10" />
          <p class="text-xs text-n-amber-11">
            {{ t('POWER_AGENTS.KNOWLEDGE.NONE_CONNECTED') }}
          </p>
        </div>

        <div v-else class="grid gap-2 sm:grid-cols-2">
          <div
            v-for="store in listaConectada"
            :key="store.id"
            class="flex flex-col gap-2 p-3 border rounded-xl border-n-weak bg-n-alpha-1 cursor-pointer hover:border-n-brand transition-colors"
            :title="t('POWER_AGENTS.KNOWLEDGE.OPEN')"
            @click="abrirBase(store)"
          >
            <div class="flex items-center gap-2">
              <span
                class="flex items-center justify-center rounded-lg size-8 shrink-0 bg-n-brand/10"
              >
                <i class="text-sm i-lucide-library text-n-brand" />
              </span>
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ store.name }}
              </span>
            </div>
            <p class="text-xs text-n-slate-10">
              {{
                t('POWER_AGENTS.KNOWLEDGE.COUNTS', {
                  fontes: store.source_count ?? 0,
                  trechos: store.chunks_count ?? 0,
                })
              }}
            </p>
            <!-- @click.stop: sem isto, desconectar também navega para a base. -->
            <span class="self-start" @click.stop>
              <Button
                :label="t('POWER_AGENTS.KNOWLEDGE.DISCONNECT')"
                :is-loading="mexendo[store.id]"
                slate
                link
                xs
                @click="desligar(store)"
              />
            </span>
          </div>
        </div>
      </div>

      <!-- Demais bases da conta -->
      <div class="flex flex-col gap-2">
        <div class="flex items-center justify-between gap-3">
          <p
            class="text-xs font-medium tracking-wide uppercase text-n-slate-11"
          >
            {{ t('POWER_AGENTS.KNOWLEDGE.AVAILABLE_TITLE') }}
          </p>
          <!-- Antes daqui só saía uma frase mandando o usuário procurar o PowerData por
               conta própria, do outro lado do menu. -->
          <Button
            v-if="podeCriarBase"
            :label="t('POWER_AGENTS.KNOWLEDGE.NEW_STORE')"
            icon="i-lucide-plus"
            slate
            faded
            xs
            class="shrink-0"
            @click="novaBase"
          />
          <span v-else class="text-xs text-n-slate-10">
            {{ t('POWER_DATA.REQUEST_BASE') }}
          </span>
        </div>
        <p v-if="!stores.length" class="text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.KNOWLEDGE.NO_STORES') }}
        </p>
        <p v-else-if="!listaDisponivel.length" class="text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.KNOWLEDGE.ALL_CONNECTED') }}
        </p>
        <div v-else class="flex flex-col gap-1.5">
          <div
            v-for="store in listaDisponivel"
            :key="store.id"
            class="flex items-center gap-3 px-3 py-2.5 border rounded-xl border-n-weak cursor-pointer hover:border-n-brand transition-colors"
            :title="t('POWER_AGENTS.KNOWLEDGE.OPEN')"
            @click="abrirBase(store)"
          >
            <i class="text-sm i-lucide-library text-n-slate-10" />
            <div class="flex-1 min-w-0">
              <span class="text-sm font-medium text-n-slate-12">
                {{ store.name }}
              </span>
              <p class="text-xs text-n-slate-10">
                {{
                  t('POWER_AGENTS.KNOWLEDGE.COUNTS', {
                    fontes: store.source_count ?? 0,
                    trechos: store.chunks_count ?? 0,
                  })
                }}
              </p>
            </div>
            <span @click.stop>
              <Button
                :label="t('POWER_AGENTS.KNOWLEDGE.CONNECT')"
                icon="i-lucide-plus"
                :is-loading="mexendo[store.id]"
                slate
                faded
                xs
                @click="ligar(store)"
              />
            </span>
          </div>
        </div>
      </div>

      <p class="text-xs text-n-slate-10">
        {{ t('POWER_AGENTS.KNOWLEDGE.HINT') }}
      </p>
    </template>

    <!-- O mesmo diálogo do PowerData, para que "criar base" signifique a mesma coisa nos
         dois lugares. `carregar` de novo porque a base nova nasce desconectada. -->
    <PowerDataModal ref="modalRef" @created="carregar" />
  </SectionShell>
</template>
